import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import SEOMeta from "../components/SEOMeta";

const WHAT_WE_DO = [
  {
    n: "01",
    title: "Corporate strategy",
    body: "Mandate, vision, and target operating model — for organisations rethinking what they should be."
  },
  {
    n: "02",
    title: "Public-sector and SOE advisory",
    body: "Governance reform, mandate review, and value-for-money in state-owned and parastatal organisations."
  },
  {
    n: "03",
    title: "Resource-sector strategy",
    body: "Commercial and operating strategy for mining, oil and gas, and extractive-adjacent businesses."
  }
];

const SECTORS = [
  { label: "Mining and minerals", slug: "mining-and-minerals" },
  { label: "Oil and gas", slug: "oil-and-gas" },
  { label: "State-owned enterprises and parastatals", slug: "state-owned-enterprises" },
  { label: "Infrastructure and utilities", slug: "infrastructure-and-utilities" },
  { label: "Industrial and extractive-adjacent", slug: "industrial-and-extractive-adjacent" }
];

const WHY = [
  {
    title: "Specialist focus",
    body: "Our practice is built for the public sector and the resource economy. We do not chase work outside that."
  },
  {
    title: "Senior-led delivery",
    body: "The partner who wins the engagement is the partner who runs the work."
  },
  {
    title: "Africa-resident, internationally connected",
    body: "Headquartered in Kampala, with affiliation to an international group of companies."
  }
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEOMeta
        title="Global Consulting for Corporate Strategy, Mining & Oil and Gas"
        description="Dugavu Ventures is a global consulting firm specialising in corporate strategy, minerals, mining, and oil and gas advisory. Based in Kampala, we partner with state-owned enterprises and resource-sector companies across Africa."
        url="/"
      />
      {/* Hero — two-column, restrained scale */}
      <section className="pt-24 md:pt-28 pb-20 md:pb-24 border-b border-rule" data-testid="home-hero">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="eyebrow page-enter">Strategy advisory · Kampala</div>

              <h1
                className="display-1 mt-6 text-[34px] sm:text-4xl md:text-5xl lg:text-[56px] xl:text-6xl text-ink leading-[1.05] page-enter delay-1"
                data-testid="home-headline"
              >
                Strategy and execution for Africa's <span className="italic text-oxblood">state-owned enterprises</span> and resource-sector companies.
              </h1>

              <p className="mt-8 text-lg text-charcoal leading-relaxed max-w-xl page-enter delay-2">
                Dugavu Ventures is a specialist advisory firm based in Kampala. We work alongside boards, chief executives, and ministerial leadership to set direction, strengthen governance, and turn strategy into delivered results.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-5 page-enter delay-3">
                <a
                  href="mailto:mandates@dugavuventures.com"
                  data-testid="home-hero-cta"
                  className="group inline-flex items-center gap-3 bg-oxblood text-white px-7 py-4 text-[12px] tracking-widest uppercase font-medium hover:bg-oxblood-hover transition-colors duration-300"
                >
                  Discuss a mandate
                  <ArrowRight size={16} weight="thin" className="transition-transform group-hover:translate-x-1" />
                </a>
                <Link
                  to="/practice"
                  data-testid="home-hero-secondary"
                  className="inline-flex items-center gap-2 text-[12px] tracking-widest uppercase font-medium text-ink link-rule"
                >
                  See our practice
                  <ArrowUpRight size={14} weight="thin" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 page-enter delay-2">
              <div className="relative aspect-[4/5] overflow-hidden bg-earth-dark/20">
                <img
                  src="https://images.pexels.com/photos/2760241/pexels-photo-2760241.jpeg?auto=compress&cs=tinysrgb&w=1200&q=80"
                  alt="Industrial operation at dusk"
                  className="w-full h-full object-cover photo-treat"
                  loading="eager"
                />
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-ink/70 to-transparent">
                  <div className="text-[10px] tracking-widest uppercase font-mono text-bone/80">Field · East Africa</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24 md:py-32" data-testid="home-what-we-do">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-3">
              <div className="eyebrow">What we do</div>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink max-w-3xl">
                Three points of entry. One commitment to delivery.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-rule border-t border-b border-rule">
            {WHAT_WE_DO.map((item) => (
              <div
                key={item.n}
                className="bg-bone p-8 md:p-10 flex flex-col group hover:bg-subtle transition-colors duration-500"
                data-testid={`what-we-do-${item.n}`}
              >
                <div className="mb-8">
                  <span className="numeral text-3xl">{item.n}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">{item.title}</h3>
                <p className="mt-5 text-charcoal leading-relaxed">{item.body}</p>
                <div className="mt-auto pt-10 flex justify-end">
                  <Link
                    to="/practice"
                    className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-oxblood link-rule"
                  >
                    See practice
                    <ArrowUpRight size={12} weight="thin" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-24 md:py-32 bg-oxblood text-bone grain relative" data-testid="home-who-we-serve">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-widest uppercase font-mono text-earth">Who we serve</div>
              <h2 className="display-2 mt-4 text-4xl md:text-5xl lg:text-6xl text-bone">
                Five sectors. Chosen, not opportunistic.
              </h2>
              <p className="mt-8 text-bone/70 leading-relaxed max-w-sm">
                Select a sector to read how we work in it.
              </p>
            </div>
            <div className="md:col-span-8 md:pl-8 md:border-l md:border-bone/20">
              <ul className="divide-y divide-bone/20">
                {SECTORS.map((s, i) => (
                  <li key={s.slug} data-testid={`sector-tag-${i}`}>
                    <Link
                      to={`/sectors#${s.slug}`}
                      data-testid={`sector-link-${s.slug}`}
                      className="group flex items-center gap-6 py-6 transition-colors duration-300 hover:bg-bone/5 -mx-2 px-2"
                    >
                      <span className="numeral text-earth text-lg w-10 transition-transform duration-300 group-hover:translate-x-1">0{i + 1}</span>
                      <span className="font-serif text-2xl md:text-3xl text-bone flex-1 group-hover:text-earth transition-colors duration-300">
                        {s.label}
                      </span>
                      <ArrowUpRight
                        size={22}
                        weight="thin"
                        className="text-bone/50 group-hover:text-earth transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/sectors"
                className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-widest uppercase font-mono text-earth border-b border-earth/40 hover:border-earth pb-1 transition-colors"
                data-testid="home-sectors-link"
              >
                Explore sectors
                <ArrowUpRight size={14} weight="thin" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dugavu */}
      <section className="py-24 md:py-32" data-testid="home-why">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-3">
              <div className="eyebrow">Why Dugavu</div>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink max-w-3xl">
                What you should expect from a specialist firm.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {WHY.map((w, i) => (
              <div key={w.title} className="border-t border-oxblood pt-8" data-testid={`why-block-${i}`}>
                <div className="numeral text-2xl">0{i + 1}</div>
                <h3 className="mt-6 font-serif text-2xl text-ink leading-tight">{w.title}</h3>
                <p className="mt-4 text-charcoal leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement & procurement */}
      <section className="py-24 md:py-32 bg-subtle" data-testid="home-procurement">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
            <div className="md:col-span-4">
              <div className="eyebrow">Engagement & procurement</div>
              <h2 className="display-2 mt-4 text-3xl md:text-4xl lg:text-5xl text-ink">
                Built for institutional buyers.
              </h2>
            </div>
            <div className="md:col-span-8">
              <p className="font-serif text-2xl md:text-[28px] text-ink leading-snug max-w-3xl">
                Dugavu Ventures Ltd is incorporated in Uganda and aligned to the procurement standards of the Public Procurement and Disposal of Public Assets Authority. We can engage through competitive tender, framework agreement, or direct mandate. To request a capability statement or discuss a procurement, write to{" "}
                <a
                  href="mailto:rfp@dugavuventures.com"
                  data-testid="procurement-rfp-link"
                  className="text-oxblood link-rule"
                >
                  rfp@dugavuventures.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
